import React, { useState } from "react";
import { Alert, Box, Button, Snackbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "./Footer";
import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import { useGetItemByIdQuery, useDeleteItemMutation } from "../../services/items/items";
import { Item, ItemMobile } from "../../components/Item/Item";
import ItemMaintenanceForm from "./ItemMaintenanceForm";
import { ItemType } from "../../types/types";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import { GREEN } from "../../styles/mui-theme";
import { useAddToCart } from "../shared/utils";
import { ITEM_MAINTENANCE_TEXTS, SNACKBAR_AUTO_HIDE_MS } from "../shared/constants";
import { CommonBoxStyles, SaveActionBar } from "../shared/styledComponents";
import DeleteItemDialog from "./DeleteItemDialog";

export const ItemMaintenancePage: React.FC = () => {
  useUpdateAuthToken();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const addToCart = useAddToCart();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { data: item, isFetching, isError } = useGetItemByIdQuery(id ?? "", { skip: !id });
  const [deleteItem, { isLoading: isDeleting }] = useDeleteItemMutation();

  const handleConfirmDelete = async () => {
    if (!id) return;
    try {
      await deleteItem(id).unwrap();
      navigate("/item-maintenance");
    } catch {
      setErrorMsg(ITEM_MAINTENANCE_TEXTS.deleteError);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <PageLayout showSearch={false}>
      {!id ? (
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {ITEM_MAINTENANCE_TEXTS.missingId}
        </Typography>
      ) : isFetching && !item ? (
        <CommonBoxStyles>
          <Box p={2}>
            <Typography variant="h5" fontWeight="bold" color={GREEN}>
              {"..."}
            </Typography>
          </Box>
        </CommonBoxStyles>
      ) : isError || !item ? (
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {ITEM_MAINTENANCE_TEXTS.notFound}
        </Typography>
      ) : (
        (() => {
          const safeItem = item as ItemType;
          const handleAdd = () => addToCart(safeItem);

          return (
            <Box>
              <CommonBoxStyles>
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                  {isSmall ? (
                    <ItemMobile
                      item={safeItem}
                      onAddToCart={handleAdd}
                      handlePurchase={handleAdd}
                    />
                  ) : (
                    <Item item={safeItem} onAddToCart={handleAdd} handlePurchase={handleAdd} />
                  )}
                </Typography>
              </CommonBoxStyles>
              <CommonBoxStyles>
                <ItemMaintenanceForm id={id} />
              </CommonBoxStyles>
            </Box>
          );
        })()
      )}
      {item && (
        <CommonBoxStyles>
          <SaveActionBar>
            <Button variant="contained" color="error" onClick={() => setDeleteDialogOpen(true)}>
              {ITEM_MAINTENANCE_TEXTS.deleteButton}
            </Button>
          </SaveActionBar>
        </CommonBoxStyles>
      )}
      <Footer />

      <DeleteItemDialog
        open={deleteDialogOpen}
        itemName={item?.name ?? ""}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
        onClose={() => setErrorMsg("")}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default ItemMaintenancePage;
